import type { ReactNode } from 'react'

import { SalesSummaryGroupBy } from '../types'
import type { SalesSummaryReport, SalesSummaryRow } from '../types'
import { ledgerPeriod } from './report-format'
import { ReportTable } from './report-table'
import type { ReportColumn, ReportRow } from './report-table'

/**
 * The legacy "Daily Sales (By Location)" sheet, column for column and in its
 * wording: Date, Invoice Count, Total Amount, Discount, Total After Discount,
 * Specific Tax, VAT, Service Charge, Net Amount — every one printed, zeros
 * included, so the sheet can be laid against the old one.
 *
 * NexQR charges no specific tax and no service charge, so those two print as
 * zeros, as on the register. The delivery fee, which the legacy had no column
 * for, sits after the service charge only when there was one, and refunds —
 * never netted into Net — follow Net only in a period that had any.
 */
function summaryColumns(groupBy: SalesSummaryGroupBy): ReportColumn<SalesSummaryRow>[] {
  return [
    {
      id: 'period',
      header: groupBy === SalesSummaryGroupBy.Month ? 'Month' : 'Date',
      value: (row) => ledgerPeriod(row.period),
    },
    { id: 'invoices', header: 'Invoice Count', format: 'whole', value: (row) => row.invoices },
    { id: 'total', header: 'Total Amount', format: 'usd', value: (row) => row.totalAmount },
    { id: 'discount', header: 'Discount', format: 'usd', value: (row) => row.discount },
    {
      id: 'afterDiscount',
      header: 'Total After Discount',
      format: 'usd',
      value: (row) => row.afterDiscount,
    },
    { id: 'specificTax', header: 'Specific Tax', format: 'usd', value: () => 0 },
    { id: 'vat', header: 'VAT', format: 'usd', value: (row) => row.vat },
    { id: 'serviceCharge', header: 'Service Charge', format: 'usd', value: () => 0 },
    {
      id: 'delivery',
      header: 'Delivery',
      format: 'usd',
      value: (row) => row.delivery,
      hideWhenEmpty: true,
    },
    { id: 'net', header: 'Net Amount', format: 'usd', value: (row) => row.net },
    {
      id: 'refunds',
      header: 'Refunds',
      format: 'whole',
      value: (row) => row.refundCount,
      hideWhenEmpty: true,
    },
    {
      id: 'refundAmount',
      header: 'Refunded',
      format: 'usd',
      value: (row) => row.refundAmount,
      hideWhenEmpty: true,
    },
  ]
}

/** What each block of a summary is, and what the whole of them is called. */
export interface SummaryGrouping {
  /**
   * What a block is — "Location", "Employee". Heads each block as
   * "Location: …", and is the first column's heading when collapsed.
   */
  header: string
  /** The grand total's label when there are several blocks — "All locations". */
  allLabel: string
}

const LOCATIONS: SummaryGrouping = { header: 'Location', allLabel: 'All locations' }

interface SalesSummaryTableProps {
  report: SalesSummaryReport
  /**
   * What the blocks are. Locations by default, which is what the server groups
   * by; the employee and customer summaries are the register regrouped — see
   * `summariseRegister`.
   */
  grouping?: SummaryGrouping
  /**
   * One line per block, its subtotal, with no periods under it — the legacy
   * "Daily Sales By Location Summary", and the one-row-per-employee report.
   */
  collapsed?: boolean
  footer?: ReactNode
}

/**
 * R1 — one row per trading day (or month), per location.
 *
 * Every block reads as the legacy sheet does: a "Location: …" heading, then
 * its days. Several blocks each close on a subtotal; a single block does not —
 * its subtotal IS the period total, and printing the same figures twice under
 * two labels invites somebody to add them.
 *
 * Refunds sit in their own columns and are never netted into Net. A refund
 * taken on the 14th for a sale on the 2nd belongs to neither day's takings,
 * and folding it in would make the 2nd's figures change after it was closed.
 */
export function SalesSummaryTable({
  report,
  grouping = LOCATIONS,
  collapsed = false,
  footer,
}: SalesSummaryTableProps) {
  const isMany = report.branches.length > 1
  const columns = summaryColumns(report.groupBy)

  if (collapsed) {
    const rows: ReportRow<SalesSummaryRow>[] = report.branches.map((branch) => ({
      id: `${branch.branchId}:subtotal`,
      data: branch.subtotal,
      label: branch.branchName,
    }))

    // The same rule as the expanded table: one block's line IS the total.
    if (isMany) {
      rows.push({ id: 'total', data: report.total, label: grouping.allLabel, tone: 'total' })
    }

    return (
      <ReportTable
        ariaLabel={report.meta.title}
        columns={[{ ...columns[0], header: grouping.header }, ...columns.slice(1)]}
        rows={rows}
        footer={footer}
      />
    )
  }

  const rows: ReportRow<SalesSummaryRow>[] = report.branches.flatMap((branch) => [
    {
      id: `${branch.branchId}:head`,
      data: null,
      label: `${grouping.header}: ${branch.branchName}`,
      tone: 'group' as const,
    },
    ...branch.rows.map((row) => ({ id: `${branch.branchId}:${row.period}`, data: row })),
    ...(isMany
      ? [
          {
            id: `${branch.branchId}:subtotal`,
            data: branch.subtotal,
            label: `${branch.branchName} total`,
            tone: 'subtotal' as const,
          },
        ]
      : []),
  ])

  rows.push({
    id: 'total',
    data: report.total,
    label: isMany ? grouping.allLabel : 'Period total',
    tone: 'total',
  })

  return <ReportTable ariaLabel={report.meta.title} columns={columns} rows={rows} footer={footer} />
}
